import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import {
  DEFAULT_DATE_PRESET_OPTIONS,
  detectDatePreset,
  formatDatePresetShort,
  type DatePresetOption,
} from '../../../constants/datePresets';
import styles from './DatePresetFilter.module.css';

interface DatePresetFilterProps {
  dateStart: string;
  dateEnd: string;
  onChange: (start: string, end: string) => void;
  placeholder?: string;
  presetOptions?: DatePresetOption[];
  allowEmpty?: boolean;
  /** Keep the placeholder text in the trigger even when a range is selected. */
  keepPlaceholder?: boolean;
  /** Shifts the open menu to the right, in pixels. */
  menuOffsetX?: number;
}

const VIEWPORT_MARGIN = 8;

const navigationBarRight = (): number => {
  let edge = 0;
  document.querySelectorAll('aside').forEach((bar) => {
    if (window.getComputedStyle(bar).position !== 'fixed') {
      return;
    }
    const rect = bar.getBoundingClientRect();
    if (rect.width <= 0 || rect.right <= 0 || rect.left >= window.innerWidth) {
      return;
    }
    edge = Math.max(edge, rect.right);
  });
  return edge;
};

const DatePresetFilter: React.FC<DatePresetFilterProps> = ({
  dateStart,
  dateEnd,
  onChange,
  placeholder = 'Período',
  presetOptions = DEFAULT_DATE_PRESET_OPTIONS,
  allowEmpty = false,
  keepPlaceholder = false,
  menuOffsetX = 0,
}) => {
  const [open, setOpen] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [menuLeft, setMenuLeft] = useState(0);

  const hasSelection = Boolean(dateStart && dateEnd);
  const selectedPreset = hasSelection ? detectDatePreset(dateStart, dateEnd, presetOptions) : '';

  const closeFilter = useCallback(() => {
    setOpen(false);
    setShowCustomInputs(false);
  }, []);

  useOutsideClick(containerRef, closeFilter);
  useEscapeToClose(closeFilter, open);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const menu = dropdownRef.current;
    const anchor = containerRef.current;
    if (!menu || !anchor) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const menuWidth = menu.offsetWidth;
    const minLeft = navigationBarRight() + VIEWPORT_MARGIN;
    const maxLeft = Math.max(minLeft, window.innerWidth - VIEWPORT_MARGIN - menuWidth);
    let left = anchorRect.left + menuOffsetX;

    if (left + menuWidth > window.innerWidth - VIEWPORT_MARGIN) {
      left = anchorRect.right - menuWidth;
    }

    left = Math.min(Math.max(left, minLeft), maxLeft);
    setMenuLeft(Math.round(left - anchorRect.left));
  }, [open, showCustomInputs, menuOffsetX]);

  const handleOpen = () => {
    if (!open) {
      setCustomStart(dateStart);
      setCustomEnd(dateEnd);
      setShowCustomInputs(hasSelection && selectedPreset === 'custom');
    }
    setOpen((current) => !current);
  };

  const handleSelectPreset = (preset: string) => {
    if (preset === 'custom') {
      setCustomStart(dateStart);
      setCustomEnd(dateEnd);
      setShowCustomInputs(true);
      return;
    }

    setShowCustomInputs(false);
    const option = presetOptions.find((item) => item.value === preset);
    if (!option?.resolveRange) {
      return;
    }

    const range = option.resolveRange();
    onChange(range.start, range.end);
    setOpen(false);
  };

  const handleApplyCustom = () => {
    if (customStart && customEnd) {
      onChange(customStart, customEnd);
      setOpen(false);
      setShowCustomInputs(false);
    }
  };

  const isOptionActive = (preset: string): boolean => {
    if (preset === 'custom') {
      return showCustomInputs || selectedPreset === 'custom';
    }

    if (showCustomInputs) {
      return false;
    }

    return selectedPreset === preset;
  };

  const activeOption = hasSelection
    ? presetOptions.find((option) => option.value === selectedPreset)
    : undefined;

  const selectionLabel = activeOption && selectedPreset !== 'custom'
    ? activeOption.label
    : hasSelection
      ? `${formatDatePresetShort(dateStart)} – ${formatDatePresetShort(dateEnd)}`
      : placeholder;

  const triggerLabel = keepPlaceholder || (!hasSelection && allowEmpty)
    ? placeholder
    : selectionLabel;

  const triggerCls = [styles.trigger, open ? styles.triggerOpen : ''].filter(Boolean).join(' ');

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <button
        type="button"
        className={triggerCls}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={hasSelection ? `${placeholder}: ${selectionLabel}` : placeholder}
      >
        <span className={!hasSelection ? styles.triggerPlaceholder : styles.triggerValue}>
          {triggerLabel}
        </span>
        <span
          className={`${styles.chevron} ${open ? styles.chevronUp : ''} material-symbols-outlined`}
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          style={{ left: menuLeft }}
          role="dialog"
        >
          <ul className={styles.list} role="listbox">
            {presetOptions.map((preset) => {
              const active = isOptionActive(preset.value);
              return (
                <li
                  key={preset.value}
                  role="option"
                  aria-selected={active}
                  className={[styles.option, active ? styles.optionActive : ''].filter(Boolean).join(' ')}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelectPreset(preset.value);
                  }}
                >
                  {preset.label}
                  {active && (
                    <span
                      className={`${styles.checkIcon} material-symbols-outlined`}
                      aria-hidden="true"
                    >
                      check
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          {showCustomInputs && (
            <div className={styles.customSection}>
              <div className={styles.customRow}>
                <div className={styles.customField}>
                  <label className={styles.customLabel}>De</label>
                  <input
                    type="date"
                    className={styles.customInput}
                    value={customStart}
                    onChange={(event) => setCustomStart(event.target.value)}
                  />
                </div>
                <div className={styles.customField}>
                  <label className={styles.customLabel}>Até</label>
                  <input
                    type="date"
                    className={styles.customInput}
                    value={customEnd}
                    onChange={(event) => setCustomEnd(event.target.value)}
                  />
                </div>
              </div>
              <button
                type="button"
                className={styles.applyBtn}
                onClick={handleApplyCustom}
                disabled={!customStart || !customEnd}
              >
                Aplicar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatePresetFilter;
